import React, { createContext, useContext, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Snackbar, Box, Slide } from '@mui/material'

const SlideLeft = (props) => <Slide {...props} direction="left" />

const MAX_TOASTS = 3
const DEFAULT_DURATION = 5000

const ToastContext = createContext({
    showToast: () => {},
    closeToast: () => {},
})

const ToastBellIcon = () => (
    <svg width="24" height="24" viewBox="3 4 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M19.0952 17.3708C18.705 16.6986 18.1249 14.7966 18.1249 12.3125C18.1249 10.8207 17.5323 9.38992 16.4774 8.33502C15.4225 7.28013 13.9918 6.6875 12.4999 6.6875C11.0081 6.6875 9.57733 7.28013 8.52244 8.33502C7.46755 9.38992 6.87492 10.8207 6.87492 12.3125C6.87492 14.7973 6.29414 16.6986 5.9039 17.3708C5.80425 17.5417 5.75142 17.7358 5.75074 17.9336C5.75006 18.1315 5.80156 18.326 5.90003 18.4975C5.99851 18.6691 6.14049 18.8117 6.31165 18.9109C6.48281 19.0101 6.67709 19.0624 6.87492 19.0625H9.74437C9.87415 19.6975 10.2193 20.2683 10.7214 20.6781C11.2235 21.088 11.8518 21.3119 12.4999 21.3119C13.1481 21.3119 13.7763 21.088 14.2784 20.6781C14.7806 20.2683 15.1257 19.6975 15.2555 19.0625H18.1249C18.3227 19.0622 18.5169 19.0098 18.6879 18.9106C18.859 18.8114 19.0009 18.6688 19.0993 18.4972C19.1977 18.3257 19.2491 18.1312 19.2484 17.9335C19.2477 17.7357 19.1949 17.5416 19.0952 17.3708ZM12.4999 20.1875C12.151 20.1874 11.8108 20.0792 11.5259 19.8777C11.2411 19.6762 11.0257 19.3914 10.9094 19.0625H14.0904C13.9741 19.3914 13.7587 19.6762 13.4739 19.8777C13.1891 20.0792 12.8488 20.1874 12.4999 20.1875ZM6.87492 17.9375C7.41632 17.0066 7.99992 14.8494 7.99992 12.3125C7.99992 11.119 8.47402 9.97443 9.31794 9.13052C10.1619 8.28661 11.3064 7.8125 12.4999 7.8125C13.6934 7.8125 14.838 8.28661 15.6819 9.13052C16.5258 9.97443 16.9999 11.119 16.9999 12.3125C16.9999 14.8473 17.5821 17.0045 18.1249 17.9375H6.87492Z" fill="#424B5A"/>
        <circle cx="17" cy="9" r="3" fill="#010101"/>
    </svg>
)

export const ToastProvider = ({ children }) => {
    const { formatMessage } = useIntl()
    const [toasts, setToasts] = useState([])

    const showToast = useCallback(({ message, body = null, actionUrl = null, severity = 'info', duration = DEFAULT_DURATION, onAction = null }) => {
        const id = Date.now() + Math.random()

        console.log('[Toast] showToast called:', { message, severity, id })

        setToasts((currentToasts) => {
            const newToasts = currentToasts.length >= MAX_TOASTS
                ? [...currentToasts.slice(1), { id, message, body, actionUrl, severity, duration, onAction }]
                : [...currentToasts, { id, message, body, actionUrl, severity, duration, onAction }]
            console.log('[Toast] New toasts state:', newToasts)
            return newToasts
        })

        return id
    }, [])

    const removeToast = useCallback((id) => {
        setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id))
    }, [])

    const closeToast = removeToast

    const handleClose = (id, event, reason) => {
        if (reason === 'clickaway') {
            return
        }
        removeToast(id)
    }

    const handleAction = (toast) => {
        if (toast.onAction) {
            toast.onAction()
        }
        removeToast(toast.id)
    }

    const handleDismiss = (e, toast) => {
        e.stopPropagation()
        removeToast(toast.id)
    }

    return (
        <ToastContext.Provider value={{ showToast, closeToast }}>
            {children}
            {toasts.map((toast, index) => (
                <Snackbar
                    key={toast.id}
                    open={true}
                    autoHideDuration={toast.duration}
                    onClose={(event, reason) => handleClose(toast.id, event, reason)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    TransitionComponent={SlideLeft}
                    style={{
                        top: `${24 + (index * 140)}px`,
                        zIndex: 999999
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            fontFamily: "'Gordita', sans-serif",
                            backgroundColor: '#F6F2F0',
                            border: '1px solid #e8e8e8',
                            borderRadius: '2px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            overflow: 'hidden',
                            minWidth: '400px',
                            maxWidth: '460px',
                            color: '#212121',
                            padding: '20px 24px',
                            gap: '16px',
                        }}
                    >
                        <Box
                            onClick={() => handleAction(toast)}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '16px',
                                flex: 1,
                                cursor: toast.onAction ? 'pointer' : 'default',
                            }}
                        >
                            <Box sx={{ paddingTop: '2px' }}>
                                <ToastBellIcon />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ fontWeight: 400, fontSize: '14px', color: '#010101', lineHeight: '20.13px', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                    {toast.message}
                                </Box>
                                {toast.body && (
                                    <Box sx={{ fontSize: '10px', color: '#010101', marginTop: '8px', lineHeight: '140%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                        {toast.body}
                                    </Box>
                                )}
                                {toast.actionUrl && (
                                    <a
                                        href={toast.actionUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={e => e.stopPropagation()}
                                        style={{ display: 'inline-block', marginTop: '12px', fontSize: '10px', color: '#010101', lineHeight: '140%', textDecoration: 'underline', textDecorationStyle: 'solid', textUnderlineOffset: '0%' }}
                                    >
                                        {formatMessage({ id: 'notifications.toast.learnMore' })}
                                    </a>
                                )}
                            </Box>
                        </Box>

                        <Box
                            onClick={(e) => handleDismiss(e, toast)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: '#9e9e9e',
                                fontSize: '18px',
                                flexShrink: 0,
                                padding: '0 0 0 4px',
                                '&:hover': {
                                    color: '#424b5a',
                                }
                            }}
                        >
                            ✕
                        </Box>
                    </Box>
                </Snackbar>
            ))}
        </ToastContext.Provider>
    )
}

export const useToast = () => {
    const context = useContext(ToastContext)
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider')
    }
    return context
}

export default ToastContext
